/**
 * TrainingEvidenceDrawer — Batch 18-F15.2 (base) · F15.3 (revocation
 * rows) · F15.4 (artifact-title hydration) · F16 (visual
 * differentiation + summary header + improved empty state).
 *
 * Manager-only side-sheet that renders a privacy-safe timeline of
 * training-evidence rows for a single technician. Backed exclusively
 * by data already loaded by the parent surface — this component does
 * NOT issue queries, does NOT call mutations, and does NOT read any
 * extension or provider evidence (those tables either don't ship row
 * data the parent has, or — for provider — don't exist yet).
 *
 * Trust classes are kept visually separate (Batch 18-F16):
 *   - manual_completion      neutral/amber accent + UserCheck icon
 *                            (the technician's own attestation —
 *                            self-attested)
 *   - manager_verification   positive/emerald accent + ShieldCheck
 *                            icon (human-attested verification)
 *   - manager_revocation     caution/slate accent + ShieldOff icon
 *                            (human or system reversal)
 *
 * Color is NEVER the sole indicator: every row carries an icon, a
 * label, AND a provenance Badge naming the trust class. A
 * grayscale/color-blind reader still gets unambiguous information.
 *
 * Privacy contract — this component:
 *   - reads only the typed `TrainingEvidenceDisplayRow[]` array
 *   - the row type by construction excludes raw URLs, tokens,
 *     payloads, headers, signatures, full notes (notes were already
 *     truncated by the row builders), provider details
 *   - never console.logs row content
 *   - never persists anywhere
 *   - never emits a postMessage / chrome.runtime / network call
 *
 * Manager-only enforcement: this component does NOT perform a role
 * check of its own. It MUST be mounted only inside a surface that is
 * already manager-gated (e.g. TechnicianTrainingVerificationCard
 * which is gated by useFeatureFlag('managerTrainingVerification')).
 * A technician-side mount would be a routing bug — and is asserted
 * against by the F15.2 source-pin.
 *
 * This component does NOT:
 *   - call useVerifyTrainingCompletion / useRevokeTrainingCompletion
 *     or any other mutation hook
 *   - write to training_consumption_sessions
 *   - write to training_completion_verifications
 *   - read or write training_effectiveness_events
 *   - reference session_token / extension_evidence / Companion code
 *   - reference provider_completion_callback / lms_completion
 */

import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Printer,
  ShieldCheck,
  ShieldOff,
  UserCheck,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  screenReaderLabelForKind,
  summarizeTrainingEvidenceRows,
  type TrainingEvidenceDisplayRow,
} from '@/utils/training/evidenceDisplayRows';

// ─── Batch 18-F28: filter tabs ───────────────────────────────────────
//
// Four-tab presentation filter for the existing manager-only evidence
// drawer. The filter is purely presentational: it does not alter the
// trust class of any row, does not promote or demote provenance, and
// does not fetch new data. Counts are derived from the same pure
// summary helper that already powers the on-screen badge row.

export type TrainingEvidenceFilter =
  | 'all'
  | 'manual'
  | 'verified'
  | 'revoked';

const ROW_KIND_BY_FILTER: Record<
  Exclude<TrainingEvidenceFilter, 'all'>,
  TrainingEvidenceDisplayRow['kind']
> = {
  manual: 'manual_completion',
  verified: 'manager_verification',
  revoked: 'manager_revocation',
};

const FILTER_LABELS: Record<TrainingEvidenceFilter, string> = {
  all: 'All',
  manual: 'Manual',
  // OD-35: manager_verification rows do not carry a named verifier here, so the filter says what they are.
  verified: 'Confirmed by Manager',
  revoked: 'Revoked',
};

function filterEvidenceRows(
  rows: ReadonlyArray<TrainingEvidenceDisplayRow>,
  filter: TrainingEvidenceFilter,
): TrainingEvidenceDisplayRow[] {
  if (filter === 'all') return [...rows];
  const wantKind = ROW_KIND_BY_FILTER[filter];
  return rows.filter((r) => r.kind === wantKind);
}

interface TrainingEvidenceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technicianName?: string;
  rows: TrainingEvidenceDisplayRow[];
}

function formatOccurredAt(value: string | null): string | null {
  if (!value) return null;
  try {
    return format(parseISO(value), 'MMM d, yyyy h:mma');
  } catch {
    return null;
  }
}

// ─── Batch 18-F16: per-kind visual treatment ─────────────────────────
//
// Each trust class gets a distinct icon + accent border + soft
// background tint + provenance-badge variant. The classes use
// Tailwind utilities only — no custom CSS file. Color choices follow
// the existing F15.2 baseline (emerald for verification, amber for
// manual, slate for revocation) and are paired with an icon + text
// label so color is NEVER the sole differentiator.

interface KindVisual {
  Icon: typeof ShieldCheck;
  iconClass: string;
  rowAccentClass: string;
  provenanceBadgeClass: string;
}

const KIND_VISUAL_BY_ROW: Record<
  TrainingEvidenceDisplayRow['kind'],
  KindVisual
> = {
  manager_verification: {
    Icon: ShieldCheck,
    iconClass: 'text-emerald-600',
    rowAccentClass: 'border-l-4 border-l-emerald-500 bg-emerald-50/40',
    provenanceBadgeClass:
      'border-emerald-200 text-emerald-700 bg-emerald-50',
  },
  manager_revocation: {
    Icon: ShieldOff,
    iconClass: 'text-slate-500',
    rowAccentClass: 'border-l-4 border-l-slate-400 bg-slate-50/40',
    provenanceBadgeClass:
      'border-slate-200 text-slate-700 bg-slate-50',
  },
  manual_completion: {
    Icon: UserCheck,
    iconClass: 'text-amber-600',
    rowAccentClass: 'border-l-4 border-l-amber-500 bg-amber-50/40',
    provenanceBadgeClass:
      'border-amber-200 text-amber-800 bg-amber-50',
  },
};

function visualForKind(
  kind: TrainingEvidenceDisplayRow['kind'],
): KindVisual {
  return KIND_VISUAL_BY_ROW[kind];
}

export function TrainingEvidenceDrawer({
  open,
  onOpenChange,
  technicianName,
  rows,
}: TrainingEvidenceDrawerProps) {
  // Stable chronological sort — most recent first; null timestamps
  // sink to the bottom so a missing occurredAt never displaces a real
  // event.
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (!a.occurredAt && !b.occurredAt) return 0;
      if (!a.occurredAt) return 1;
      if (!b.occurredAt) return -1;
      return b.occurredAt.localeCompare(a.occurredAt);
    });
  }, [rows]);

  // Batch 18-F16: summary counts derived from the same typed rows.
  // The helper is pure + deterministic; counts are by row.kind only.
  const summary = useMemo(
    () => summarizeTrainingEvidenceRows(rows),
    [rows],
  );

  // Batch 18-F22: most-recent evidence label, derived from the same
  // typed rows — no new data fetch, no new prop. The `sorted` array
  // above puts rows with the largest occurredAt first and sinks null
  // timestamps to the bottom, but the first row's occurredAt can
  // still be null when EVERY row is null (or rows is empty). The
  // explicit `find` makes that fallthrough explicit: it returns the
  // most-recent non-null timestamp or undefined, which collapses to
  // null and hides the label entirely.
  const latestEvidenceLabel = useMemo(() => {
    const newest = sorted.find((r) => r.occurredAt)?.occurredAt ?? null;
    return formatOccurredAt(newest);
  }, [sorted]);

  // Batch 18-F28: filter state + filtered rows. The filter is purely
  // presentational. Counts come from the same `summary` helper above,
  // so the tab counts and the summary badges always agree.
  const [selectedEvidenceFilter, setSelectedEvidenceFilter] =
    useState<TrainingEvidenceFilter>('all');
  const filteredEvidenceRows = useMemo(
    () => filterEvidenceRows(sorted, selectedEvidenceFilter),
    [sorted, selectedEvidenceFilter],
  );
  const filterVisibleCount = filteredEvidenceRows.length;
  const filterTotalCount = sorted.length;
  const printFilterCaption = `Filter: ${
    FILTER_LABELS[selectedEvidenceFilter]
  } (${filterVisibleCount} of ${filterTotalCount})`;
  const countByFilter: Record<TrainingEvidenceFilter, number> = {
    all: summary.total,
    manual: summary.manualCompletion,
    verified: summary.managerVerification,
    revoked: summary.managerRevocation,
  };

  // Batch 18-F18: privacy-safe print handler. Invokes window.print()
  // against the currently-rendered drawer DOM — no serialization, no
  // new window, no new route, no new fetch. The browser's print
  // dialog handles the visual transform. We guard typeof window !==
  // 'undefined' so SSR / static rendering never throws (defensive;
  // the drawer is only mounted client-side today).
  const handlePrintEvidence = () => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Batch 18-F19: print-friendly SheetContent chrome.
          print:shadow-none + print:border-0 keep the printed page
          from showing the drawer's modal card outline. The Radix-
          provided close X overlay is positioned inside SheetContent
          and is not directly controllable from here without a global
          CSS rule; it prints as a tiny icon in the corner and is
          acceptable. */}
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto print:max-w-none print:shadow-none print:border-0 print:overflow-visible"
        data-testid="training-evidence-drawer"
      >
        {/* Batch 18-F23: print-only audit header. Hidden on screen
            (`hidden`), shown only when print media is active
            (`print:block`). Gives the printed artifact a clear
            standalone-document title, technician context, and origin
            line so a page reaching a manager's desk reads as an audit
            summary, not as a UI screenshot. Uses ONLY the existing
            `technicianName` prop — no new prop, no new query, no new
            fetch. The on-screen drawer never sees this block. Privacy
            boundary: title + technician name + OptiBay context only.
            No raw IDs, URLs, tokens, signatures, or payloads. */}
        <div
          className="hidden print:block mb-4"
          data-testid="training-evidence-print-header"
        >
          <h2
            className="text-lg font-semibold"
            data-testid="training-evidence-print-header-title"
          >
            Training Evidence Summary
          </h2>
          <p
            className="text-xs text-muted-foreground"
            data-testid="training-evidence-print-header-subtitle"
          >
            {technicianName
              ? `For ${technicianName}`
              : 'Manager training evidence summary'}
          </p>
          <p
            className="text-xs text-muted-foreground"
            data-testid="training-evidence-print-header-context"
          >
            Printed from OptiBay manager training evidence.
          </p>
          {/* Batch 18-F28: print-only caption that reports which
              filter the manager has selected and how many rows that
              filter shows out of the total. Sits alongside the
              existing print-header subtitle/context — no existing
              print-header testid is removed. Reads from the same
              `selectedEvidenceFilter` state that drives the on-
              screen tabs so the printed output always matches the
              on-screen selection. */}
          <p
            className="text-xs text-muted-foreground"
            data-testid="training-evidence-print-header-filter"
          >
            {printFilterCaption}
          </p>
        </div>

        <SheetHeader>
          <SheetTitle>Training evidence</SheetTitle>
          <SheetDescription>
            {technicianName
              ? `Recent training-evidence activity for ${technicianName}.`
              : 'Recent training-evidence activity.'}
          </SheetDescription>
          {/* Batch 18-F22: most-recent evidence timestamp. Renders
              only when at least one row has a valid occurredAt; the
              `latestEvidenceLabel` useMemo collapses to null
              otherwise. Intentionally NOT print:hidden — it's a
              useful one-line context anchor for the printed audit. */}
          {latestEvidenceLabel && (
            <p
              className="mt-1 text-xs text-muted-foreground"
              aria-label={`Most recent training evidence recorded ${latestEvidenceLabel}`}
              data-testid="training-evidence-drawer-last-evidence"
            >
              Last evidence: {latestEvidenceLabel}
            </p>
          )}
        </SheetHeader>

        {/* Batch 18-F18 — Print evidence action.
            Triggers window.print() against the currently-rendered
            drawer content. NO new fetch, NO new window, NO new
            route, NO serialization of row data. Disabled when rows
            is empty so we never print an empty drawer.
            Batch 18-F19 — print:hidden hides the entire row (button
            + note) from the printed output so the print page is the
            evidence summary + list, not the action chrome that
            triggered the print. The privacy note below is
            intentionally KEPT in print: it's a short trust
            disclosure that confirms to the printed-page reader that
            this is the manager-view summary, not a raw export. */}
        <div
          className="mt-3 flex flex-wrap items-center gap-2 print:hidden"
          data-testid="training-evidence-drawer-print-row"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={rows.length === 0}
            onClick={handlePrintEvidence}
            aria-label="Print training evidence"
            data-testid="training-evidence-print-button"
          >
            <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
            Print evidence
          </Button>
          <span
            className="text-[11px] text-muted-foreground"
            data-testid="training-evidence-print-note"
          >
            Prints the evidence currently shown in this drawer.
          </span>
          {/* Batch 18-F24: visible keyboard-affordance hint.
              Discoverability only — we do NOT install custom
              shortcut listeners. Cmd/Ctrl+P is delivered by the
              browser's native print stack; Esc is delivered by the
              Radix Sheet's built-in close. The hint sits inside the
              already-print:hidden action row so it never reaches
              the printed audit; print:hidden is applied directly to
              the span too, so a later restructuring of the parent
              can't silently leak this chrome into print. <kbd>
              elements are the semantically correct vehicle for the
              key labels and are announced cleanly by screen
              readers without any aria-live theatrics. */}
          <span
            className="text-[11px] text-muted-foreground print:hidden"
            data-testid="training-evidence-keyboard-hint"
          >
            Tip:{' '}
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px] font-mono">
              Cmd/Ctrl+P
            </kbd>{' '}
            prints this evidence.{' '}
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px] font-mono">
              Esc
            </kbd>{' '}
            closes the drawer.
          </span>
        </div>

        <p
          className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground"
          data-testid="training-evidence-drawer-privacy-note"
        >
          Manager view · provenance only · no raw URLs, tokens, or
          provider payloads
        </p>

        {/* Batch 18-F16: summary header. Tells the manager at a
            glance how many of each trust class are recorded. Counts
            are derived from row.kind via the pure helper.
            Batch 18-F16.1: role="region" + aria-label make the
            summary an addressable landmark so screen readers can
            jump straight to it. */}
        <div
          className="mt-3 flex flex-wrap items-center gap-2"
          role="region"
          aria-label="Training evidence summary"
          data-testid="training-evidence-drawer-summary"
        >
          <Badge
            variant="secondary"
            className="text-[11px]"
            data-testid="training-evidence-drawer-summary-total"
          >
            {summary.total} evidence record{summary.total === 1 ? '' : 's'}
          </Badge>
          <Badge
            variant="outline"
            className="text-[11px] border-amber-200 text-amber-800 bg-amber-50"
            data-testid="training-evidence-drawer-summary-manual"
          >
            {summary.manualCompletion} manual
          </Badge>
          <Badge
            variant="outline"
            className="text-[11px] border-emerald-200 text-emerald-700 bg-emerald-50"
            data-testid="training-evidence-drawer-summary-verified"
          >
            {summary.managerVerification} verified
          </Badge>
          <Badge
            variant="outline"
            className="text-[11px] border-slate-200 text-slate-700 bg-slate-50"
            data-testid="training-evidence-drawer-summary-revoked"
          >
            {summary.managerRevocation} revoked
          </Badge>
        </div>

        <Separator className="mt-3" />

        {/* Batch 18-F28: filter tabs. Built on the shadcn Tabs
            primitive (Radix), which automatically wires
            role="tablist" / role="tab" / role="tabpanel" /
            aria-selected / aria-controls / aria-labelledby. Tab
            labels carry the count both visibly and via aria-label
            so screen-reader users hear "Verified, 2 records". The
            counts come from the same summary helper that drives
            the existing summary badges so they always agree.
            Counts are derived client-side; no new fetch. */}
        <Tabs
          value={selectedEvidenceFilter}
          onValueChange={(v) =>
            setSelectedEvidenceFilter(v as TrainingEvidenceFilter)
          }
          className="mt-3"
        >
          <TabsList
            className="grid w-full grid-cols-4 print:hidden"
            data-testid="training-evidence-filter-tabs"
          >
            {(
              [
                ['all', 'training-evidence-filter-tab-all'],
                ['manual', 'training-evidence-filter-tab-manual'],
                ['verified', 'training-evidence-filter-tab-verified'],
                ['revoked', 'training-evidence-filter-tab-revoked'],
              ] as const
            ).map(([value, testid]) => {
              const label = FILTER_LABELS[value];
              const count = countByFilter[value];
              return (
                <TabsTrigger
                  key={value}
                  value={value}
                  data-testid={testid}
                  aria-label={`${label}, ${count} ${
                    count === 1 ? 'record' : 'records'
                  }`}
                  className="text-xs"
                >
                  {label} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>

        <TabsContent
          value={selectedEvidenceFilter}
          data-testid="training-evidence-filter-panel"
        >
        <ul
          className="mt-4 space-y-3"
          data-testid="training-evidence-drawer-list"
        >
          {sorted.length === 0 && (
            <li
              className="rounded-lg border border-dashed bg-muted/30 p-4 space-y-2"
              data-testid="training-evidence-drawer-empty"
            >
              <p className="text-sm font-medium" data-testid="training-evidence-drawer-empty-title">
                No evidence records yet
              </p>
              <p className="text-xs text-muted-foreground" data-testid="training-evidence-drawer-empty-scope">
                This drawer only shows manager-visible training
                evidence already recorded for this technician: manual
                completions, manager verifications, and manager
                revocations.
              </p>
              <p className="text-xs text-muted-foreground" data-testid="training-evidence-drawer-empty-deferred">
                Extension and provider evidence are not shown here
                yet.
              </p>
              {/* Batch 18-F20: actionable guidance for a new
                  manager. Names the trigger condition for each
                  trust class so a zero-state reader understands
                  this is a "no activity yet" condition rather than
                  a broken drawer. Stays accurate: the drawer is
                  NEVER fed by automatic Companion observation;
                  manual completions are technician-driven and
                  verifications/revocations are manager-driven. */}
              <p
                className="text-xs text-muted-foreground"
                data-testid="training-evidence-drawer-empty-guidance"
              >
                Manual completions appear here once a technician
                marks training complete. Manager verifications and
                revocations appear here after a manager reviews
                that training.
              </p>
            </li>
          )}
          {/* Batch 18-F28: when there ARE rows but the active
              filter excludes them all, render a filter-specific
              neutral empty state. The existing F20 overall empty
              block above still owns the "rows.length === 0" case
              — these filter empties only fire when the drawer has
              rows but the chosen tab matches nothing. role="status"
              announces the change to screen-reader users when they
              switch to a filter that turns out to be empty. */}
          {sorted.length > 0 &&
            filteredEvidenceRows.length === 0 &&
            selectedEvidenceFilter !== 'all' && (
              <li
                role="status"
                aria-live="polite"
                className="rounded-lg border border-dashed bg-muted/30 p-4"
                data-testid={`training-evidence-drawer-filter-empty-${selectedEvidenceFilter}`}
              >
                <p className="text-xs text-muted-foreground">
                  {selectedEvidenceFilter === 'manual' &&
                    'No self-attested completions recorded yet for this artifact.'}
                  {selectedEvidenceFilter === 'verified' &&
                    'No manager verifications recorded yet for this artifact.'}
                  {selectedEvidenceFilter === 'revoked' &&
                    'No verification reversals or revocations recorded for this artifact.'}
                </p>
              </li>
            )}
          {/* Batch 18-F28: list now renders the filtered rows.
              The wrapping TabsContent (above the <ul>) tracks the
              selected filter, gets role="tabpanel" + aria-labelledby
              wiring from Radix, and renders for the active value. */}
          {filteredEvidenceRows.map((row) => {
            const dateLabel = formatOccurredAt(row.occurredAt);
            const visual = visualForKind(row.kind);
            const Icon = visual.Icon;
            const srTrustLabel = screenReaderLabelForKind(row.kind);
            return (
              <li
                key={row.id}
                /* Batch 18-F16.1: tabIndex={0} makes evidence rows
                   keyboard-focusable. They are NOT buttons (no
                   onClick / role="button") — focus is for screen-
                   reader navigation and keyboard readers, not for
                   action. The focus-visible ring matches the project
                   convention used by slider/tabs/switch/etc. */
                tabIndex={0}
                className={`rounded-lg border bg-card p-3 space-y-1 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${visual.rowAccentClass}`}
                data-testid="training-evidence-drawer-row"
                data-evidence-kind={row.kind}
              >
                {/* Batch 18-F16.1: screen-reader-only trust-class
                    label, announced before the visible content so
                    color-cue is never the sole class indicator. */}
                <span
                  className="sr-only"
                  data-testid="training-evidence-drawer-row-sr-label"
                >
                  {srTrustLabel}
                </span>
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 shrink-0">
                    <Icon
                      className={`h-4 w-4 ${visual.iconClass}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{row.label}</p>
                    {row.detail && (
                      <p className="text-xs text-muted-foreground truncate">
                        {row.detail}
                      </p>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {dateLabel && (
                        <span aria-label={`Recorded ${dateLabel}`}>
                          {dateLabel}
                        </span>
                      )}
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wide ${visual.provenanceBadgeClass}`}
                        data-testid="training-evidence-drawer-row-provenance"
                      >
                        {row.provenance}
                      </Badge>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
